import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormHelperText,
  Stack,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Switch,
  Tooltip,
  Checkbox,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { CustomTable, InputControl, usePermission } from "../../../shared";
import editIcon from "../../../assets/images/Icons/editIcon.png";
import SaveIcon from "../../../assets/images/Icons/ic_save.svg";
import leaf from "../../../assets/images/Icons/ingredient.svg";
import book from "../../../assets/images/Icons/Cookbook.svg";
import { useLocation } from "react-router-dom";
import { StockBulkUpdateTrigger } from "./StockBulkUpdateTrigger";

const columnHelper = createColumnHelper();

export const StockProductsList = ({
  selectedCategoryId,
  bulkUpdate,
  isBulkVisibilityUpdating,
  onBulkUpdateConfirm,
  updateBulkUpdateRowSelection,
  tableData = [],
  filters = { status: null, visibility: null },
  editingRowId = null,
  disableClearButton = true,
  onStatusChange,
  onVisibilityChange,
  onClearFilters,
  onEdit,
  onThresholdChange,
  onVisibilityToggle,
  onCancel,
  isSaving = false,
  validationErrors = {},
  itemEdits = {},
}) => {
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("/store-admin");
  const tableId = "stockItems" + (isStoreAdmin ? "Store" : "Admin");
  const isIngredientEditable = usePermission("ingredient-modify");
  const isRecipeEditable = usePermission("recipe-stock-take-view");
  const isStockTakeCreatable = usePermission("stock-take-create");

  // Filter options
  const statusOptions = [
    { label: "ENABLED", value: 1 },
    { label: "DISABLED", value: 0 },
  ];

  const visibilityOptions = [
    { label: "Visible", value: 1 },
    { label: "Hidden", value: 0 },
  ];

  const columns = useMemo(
    () =>
      [
        ...(isStoreAdmin
          ? [
              columnHelper.display({
                id: "index",
                header: ({ table }) => (
                  <Checkbox
                    size="small"
                    sx={{ padding: 0 }}
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                  />
                ),
                cell: ({ row }) => (
                  <Checkbox
                    size="small"
                    sx={{ padding: 0 }}
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    indeterminate={row.getIsSomeSelected()}
                    onChange={row.getToggleSelectedHandler()}
                  />
                ),
                meta: { width: "50px", align: "center" },
              }),
            ]
          : []),
        columnHelper.accessor("itemName", {
          header: "Item Name",
          cell: isStoreAdmin
            ? ({ getValue, row }) =>
                !row.depth ? (
                  <Typography
                    sx={{
                      color: "primary.main",
                      textTransform: "uppercase",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    {getValue()} ({row.original.subRows?.length || 0})
                  </Typography>
                ) : (
                  <Stack direction="row" alignItems="center" gap="6px">
                    {row.original.type === "ingredient" && (
                      <img src={leaf} alt="ingredient" width={16} height={16} />
                    )}
                    {row.original.type === "recipe" && (
                      <img src={book} alt="recipe" width={16} height={16} />
                    )}
                    <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                      {getValue()}
                    </Typography>
                  </Stack>
                )
            : ({ getValue, row }) => {
                if (!row.depth)
                  return (
                    <Typography
                      sx={{
                        color: "primary.main",
                        textTransform: "uppercase",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      {getValue()} ({row.original.subRows?.length || 0})
                    </Typography>
                  );

                const isIngredient = row.original.type === "ingredient";
                const enableRecipe = isRecipeEditable && !isIngredient;
                const enableIngredient = isIngredientEditable && isIngredient;
                const enableEdit = enableRecipe || enableIngredient;

                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap="6px"
                    sx={{
                      cursor: enableEdit ? "pointer" : "default",
                      textDecoration: enableEdit ? "underline" : "none",
                    }}
                  >
                    {row.original.type === "ingredient" && (
                      <img src={leaf} alt="ingredient" width={16} height={16} />
                    )}
                    {row.original.type === "recipe" && (
                      <img src={book} alt="recipe" width={16} height={16} />
                    )}
                    <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                      {getValue()}
                    </Typography>
                  </Stack>
                );
              },
          meta: { minWidth: "200px" },
        }),
        columnHelper.accessor("stock_code", {
          header: "Stock Code",
          meta: { width: "120px" },
          cell: ({ getValue, row }) =>
            !row.depth ? (
              ""
            ) : (
              <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                {getValue() || "--"}
              </Typography>
            ),
        }),
        columnHelper.accessor("status", {
          header: "Stocktake Status",
          meta: { width: "130px" },
          cell: ({ row }) => {
            if (!row.depth) return "";
            const isStatusActive = row?.original?.status;
            return (
              <Chip
                size="small"
                variant={isStatusActive ? "generated" : "stockTakeDraft"}
                label={isStatusActive ? "ENABLED" : "DISABLED"}
              />
            );
          },
        }),
        columnHelper.accessor("stockUnit", {
          header: "Stock Unit",
          meta: { width: "180px" },
          cell: ({ getValue, row }) =>
            !row.depth ? (
              ""
            ) : (
              <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                {getValue()}
              </Typography>
            ),
        }),
        columnHelper.accessor("cost", {
          header: "Cost (Ex GST)",
          meta: { width: "110px" },
          cell: ({ getValue, row }) =>
            !row.depth ? (
              ""
            ) : (
              <Typography sx={{ fontSize: "14px", fontWeight: 400 }}>
                {getValue()}
              </Typography>
            ),
        }),
        columnHelper.accessor("threshold_unit", {
          header: () => (
            <Stack direction="column">
              <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                Threshold
              </Typography>
              <Stack direction="row" gap="90px">
                <Typography
                  sx={{ fontSize: "11px", fontWeight: 500, color: "#8E99AC" }}
                >
                  Base Qty
                </Typography>
                <Typography
                  sx={{ fontSize: "11px", fontWeight: 500, color: "#8E99AC" }}
                >
                  Tolerance
                </Typography>
              </Stack>
            </Stack>
          ),
          enableSorting: false,
          meta: {
            width: "300px",
            borderLeft: "1px solid #DFE8F2",
            borderRight: "1px solid #DFE8F2",
            backgroundColor: "#F2F7FC",
          },
          cell: ({ row, table }) => {
            if (!row.depth) return "";
            const {
              editingRowId: rowEditId,
              itemEdits: edits,
              validationErrors: errors,
              onThresholdChange: onThresholdCh,
              onEdit: onEditRow,
              onCancel: onCancelRow,
              isSaving: saving,
            } = table.options.meta;
            const isEditing = rowEditId === row.original.id;
            const thresholdUnit =
              edits[row.original.id]?.threshold_unit ??
              row.original.threshold_unit;
            const thresholdPct =
              edits[row.original.id]?.threshold_percentage ??
              row.original.threshold_percentage;
            const qtyError = errors[row.original.id]?.threshold_unit;
            const pctError = errors[row.original.id]?.threshold_percentage;
            const hasError = !!pctError || !!qtyError;
            return (
              <Stack
                direction="row"
                gap="8px"
                alignItems="center"
                sx={{
                  backgroundColor: isEditing ? "#DFE8F2" : "#F2F7FC",
                  padding: "6px 8px",
                  margin: "-8px",
                }}
              >
                <Stack direction="column">
                  <OutlinedInput
                    type="number"
                    size="small"
                    value={thresholdUnit}
                    disabled={!isEditing}
                    error={!!qtyError}
                    onChange={(e) =>
                      onThresholdCh?.(
                        row.original.id,
                        "threshold_unit",
                        e.target.value,
                      )
                    }
                    inputProps={{ style: { MozAppearance: "textfield" } }}
                    sx={{
                      minWidth: "120px",
                      height: "36px",
                      backgroundColor: isEditing ? "#fff" : "transparent",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "neutral.10",
                      },
                      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#DFE8F2",
                      },
                      "& input": { padding: "8px 12px", fontSize: "16px" },
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                        { WebkitAppearance: "none", margin: 0 },
                      "& input[type=number]": { MozAppearance: "textfield" },
                    }}
                  />
                  {qtyError && (
                    <FormHelperText error sx={{ mx: 0, fontSize: "10px" }}>
                      {qtyError}
                    </FormHelperText>
                  )}
                </Stack>
                <Stack direction="column">
                  <OutlinedInput
                    type="number"
                    size="small"
                    value={thresholdPct}
                    disabled={!isEditing}
                    error={!!pctError}
                    onChange={(e) =>
                      onThresholdCh?.(
                        row.original.id,
                        "threshold_percentage",
                        e.target.value,
                      )
                    }
                    endAdornment={
                      <InputAdornment
                        position="end"
                        sx={{
                          backgroundColor: "#E6EEF7",
                          padding: "6px 8px",
                          margin: "0 -14px 0 0",
                          height: "100%",
                          maxHeight: "none",
                          borderLeft: "1px solid #DFE8F2",
                          borderTopRightRadius: "8px",
                          borderBottomRightRadius: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#8E99AC",
                            fontWeight: 500,
                          }}
                        >
                          %
                        </Typography>
                      </InputAdornment>
                    }
                    inputProps={{ style: { MozAppearance: "textfield" } }}
                    sx={{
                      minWidth: "120px",
                      height: "36px",
                      backgroundColor: isEditing ? "#fff" : "transparent",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "neutral.10",
                      },
                      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#DFE8F2",
                      },
                      "& input": { padding: "8px 12px", fontSize: "16px" },
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                        { WebkitAppearance: "none", margin: 0 },
                      "& input[type=number]": { MozAppearance: "textfield" },
                    }}
                  />
                  {pctError && (
                    <FormHelperText error sx={{ mx: 0, fontSize: "10px" }}>
                      {pctError}
                    </FormHelperText>
                  )}
                </Stack>
                {isStockTakeCreatable ? (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      borderColor: isEditing ? "#B0BEC5" : "#DFE8F2",
                    }}
                  />
                ) : null}

                {isEditing ? (
                  <>
                    <Tooltip title="Save" arrow>
                      <IconButton
                        size="small"
                        onClick={() => onEditRow?.(row.original)}
                        disabled={saving || hasError}
                      >
                        {saving ? (
                          <CircularProgress size={18} color="primary" />
                        ) : (
                          <Box
                            component="img"
                            src={SaveIcon}
                            height={20}
                            width={20}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel" arrow>
                      <IconButton
                        size="small"
                        onClick={() => onCancelRow?.(row.original.id)}
                        disabled={saving}
                      >
                        <Clear fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : isStockTakeCreatable ? (
                  <IconButton
                    size="small"
                    onClick={() => onEditRow?.(row.original)}
                  >
                    <Box
                      component="img"
                      src={editIcon}
                      height={20}
                      width={20}
                    />
                  </IconButton>
                ) : null}
              </Stack>
            );
          },
        }),
        columnHelper.accessor("visibility", {
          header: "Visibility",
          meta: { width: "80px", align: "right", pl: 2 },
          cell: ({ row, table }) => {
            if (!row.depth) return "";
            const { onVisibilityToggle: onVisTog } = table.options.meta;
            const isVisible = row.original.visibility;
            return (
              <Switch
                size="small"
                disabled={!isStockTakeCreatable}
                checked={isVisible === 1}
                onChange={() => onVisTog?.(row.original)}
              />
            );
          },
        }),
        columnHelper.display({
          id: "actions",
          header: "",
          cell: isStoreAdmin
            ? ({ row, table }) => {
                if (!row.depth) return "";
                const {
                  editingRowId: rowEditId,
                  onEdit: onEditRow,
                  onCancel: onCancelRow,
                  isSaving: saving,
                  validationErrors: errors,
                } = table.options.meta;
                const isEditing = rowEditId === row.original.id;
                const hasError =
                  !!errors[row.original.id]?.threshold_percentage;
                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    gap="4px"
                  >
                    {isEditing ? (
                      <>
                        <Tooltip title="Save" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onEditRow?.(row.original)}
                            disabled={saving || hasError}
                          >
                            {saving ? (
                              <CircularProgress size={18} color="primary" />
                            ) : (
                              <Box
                                component="img"
                                src={SaveIcon}
                                height={20}
                                width={20}
                              />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel" arrow>
                          <IconButton
                            size="small"
                            onClick={() => onCancelRow?.(row.original.id)}
                            disabled={saving}
                            title="Cancel"
                          >
                            <Clear fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => onEditRow?.(row.original)}
                      >
                        <Box
                          component="img"
                          src={editIcon}
                          height={20}
                          width={20}
                        />
                      </IconButton>
                    )}
                  </Stack>
                );
              }
            : ({ row }) => {
                if (!row.depth) return "";
                return <img alt="details" src="/icons/arrow_forward.png" />;
              },
        }),
      ].filter((col) => {
        const id = col.id ?? col.accessorKey;
        if (!isStoreAdmin) {
          return id !== "threshold_unit" && id !== "visibility";
        } else {
          if (id === "actions") return false;
        }

        return true;
      }),
    [isStoreAdmin],
  );

  return (
    <CustomTable
      defaultData={tableData}
      columns={columns}
      isLoading={false}
      showSearchBar={false}
      isTableHeader={false}
      disablePagination={true}
      tableMeta={{
        editingRowId,
        itemEdits,
        validationErrors,
        isSaving,
        onThresholdChange,
        onVisibilityToggle,
        onEdit,
        onCancel,
      }}
      tableId={tableId}
      subRow="subRows"
      allRowsExpanded={true}
      customRowStyle={(row) => (!row.depth ? { borderBottom: "none" } : null)}
      resetRowSelection={bulkUpdate.resetSelection || selectedCategoryId}
      onChangeRowSelection={(selectedRows) => {
        updateBulkUpdateRowSelection(selectedRows);
      }}
      tableHeaderContent={
        <Stack
          sx={{
            flexDirection: "row",
            gap: "6px",
            width: "100%",
          }}
        >
          <InputControl
            type="dropdown"
            variant="filter"
            size="small"
            options={statusOptions}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            sx={{ width: "180px", height: "36px" }}
            value={filters.status}
            onChange={onStatusChange}
            renderInput={(params) => (
              <TextField {...params} placeholder="Stocktake Status" />
            )}
          />
          {isStoreAdmin && (
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              options={visibilityOptions}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              sx={{ width: "180px", height: "36px" }}
              value={filters.visibility}
              onChange={onVisibilityChange}
              renderInput={(params) => (
                <TextField {...params} placeholder="Visibility" />
              )}
            />
          )}
          <Button
            type="button"
            size="small"
            variant="text"
            disabled={disableClearButton}
            onClick={onClearFilters}
          >
            {isStoreAdmin ? "Clear All" : "Clear"}
          </Button>
          {isStoreAdmin ? (
            <Box sx={{ ml: "auto" }}>
              <StockBulkUpdateTrigger
                loading={isBulkVisibilityUpdating}
                disabledOptions={bulkUpdate.selectedItems.length === 0}
                onConfirmBulkUpdateToggle={onBulkUpdateConfirm}
              />
            </Box>
          ) : null}
        </Stack>
      }
      onRowLink={
        !isStoreAdmin
          ? (row) => "details/".concat(row.id, "?basic-details")
          : null
      }
    />
  );
};
